<template>
  <div class="min-h-screen lg:ml-56 lg:mr-44">
    <!-- HEADING -->
    <div class="lg:h-72 flex justify-start items-end p-12 bg-gray-200">
      <div
        class="h-32 border-l-4 pl-6 border-black flex flex-col justify-end items-end m-0 p-0 w-full"
      >
        <h1 class="w-full text-left">Media Gallery</h1>
        <span class="text-md w-full text-left"
          >Add videos, images and even files to your home page media
          gallery</span
        >
      </div>
    </div>

    <!-- CONTENT -->
    <div
      class="bg-bluegray-700 w-full pb-16 flex flex-col items-center lg:items-start"
    >
      <div
        :class="loaded ? 'py-16 px-6 lg:p-16' : ''"
        class="bg-white rounded-xl lg:ml-16 mt-16 relative z-20 w-11/12 lg:w-9/12 flex flex-col flex-wrap lg:flex-nowrap min-h-screen"
      >
        <div v-if="loaded" class="w-full">
          <div class="space-y-8 flex flex-col w-full">
            <!-- NEW FILE UPLOAD -->
            <div class="w-full">
              <div
                v-if="!video_preview && !file_preview"
                class="flex space-x-2 w-full justify-start lg:justify-between"
              >
                <button
                  v-if="!file_preview_change"
                  :disabled="orderThinking || !canSaveResources"
                  :class="[
                    orderThinking
                      ? 'bg-black hover:bg-black active:bg-black cursor-not-allowed'
                      : '',
                    !canSaveResources ? 'opacity-60 cursor-not-allowed' : '',
                  ]"
                  type="submit"
                  class="btn btn-primary relative"
                  @click="save_resources"
                >
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="orderThinking"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <span :class="[orderThinking ? 'invisible' : '']"
                    >Save <span class="hidden lg:inline">Order</span></span
                  >
                </button>

                <button
                  v-if="!file_preview_change && !thinking"
                  :disabled="thinking"
                  :class="[
                    thinking ? 'thinking-primary cursor-not-allowed' : '',
                  ]"
                  type="submit"
                  class="inline lg:hidden lg:w-auto btn btn-black flex-shrink-0 relative"
                  @click="file_change(1)"
                >
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinking"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <span :class="[thinking ? 'invisible' : '']">+ File</span>
                </button>

                <button
                  v-if="!file_preview_change"
                  :disabled="thinking"
                  :class="[
                    thinking ? 'thinking-primary cursor-not-allowed' : '',
                  ]"
                  type="submit"
                  class="inline lg:hidden lg:w-auto btn btn-black flex-shrink-0 relative"
                  @click="file_change(2)"
                >
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinking"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <span :class="[thinking ? 'invisible' : '']">+ Video</span>
                </button>

                <form ref="selectFileForm">
                  <input
                    ref="selectFile"
                    type="file"
                    class="hidden"
                    @input="file_selected"
                  />
                </form>

                <div
                  class="hidden lg:flex lg:items-end lg:justify-end lg:space-x-2"
                >
                  <button
                    v-if="!file_preview_change && !thinking"
                    :disabled="thinking"
                    :class="[
                      thinking ? 'thinking-primary cursor-not-allowed' : '',
                    ]"
                    type="submit"
                    class="lg:w-auto btn btn-black flex-shrink-0 relative"
                    @click="file_change(1)"
                  >
                    <div
                      class="absolute inset-0 w-full flex justify-center items-center z-20"
                    >
                      <lottie
                        v-if="thinking"
                        :options="loadingAnimationOptions"
                      />
                    </div>
                    <span :class="[thinking ? 'invisible' : '']">Add File</span>
                  </button>

                  <button
                    v-if="!file_preview_change"
                    :disabled="thinking"
                    :class="[
                      thinking ? 'thinking-primary cursor-not-allowed' : '',
                    ]"
                    type="submit"
                    class="lg:w-auto btn btn-black flex-shrink-0 relative"
                    @click="file_change(2)"
                  >
                    <div
                      class="absolute inset-0 w-full flex justify-center items-center z-20"
                    >
                      <lottie
                        v-if="thinking"
                        :options="loadingAnimationOptions"
                      />
                    </div>
                    <span :class="[thinking ? 'invisible' : '']"
                      >Add Video</span
                    >
                  </button>
                </div>
              </div>

              <h5 v-if="feat_resources_length > 7" class="text-red-500 mt-4">
                You have reached the Featured Media Gallery file limit.
              </h5>
              <div v-if="file_abort_error" class="text-red-700">
                Please select the correct file format...
              </div>

              <!-- PREVIEW AND TITLE -->
              <div
                v-if="video_preview"
                class="relative"
                :class="show_video_preview ? 'w-min-content' : 'w-full'"
              >
                <div class="flex flex-col w-full">
                  <div v-if="show_video_preview" class="relative">
                    <h3
                      class="z-10 absolute bottom-1/2 right-1/2 transform translate-x-1/2 translate-y-1/2"
                    >
                      Loading ...
                    </h3>
                    <iframe
                      ref="resourceVideoPreview"
                      type="iframe"
                      aspect="16by9"
                      class="inset-0 w-full h-full rounded relative z-40"
                      :src="show_video_preview"
                      frameborder="0"
                      style="height: 300px; width: 409px"
                    ></iframe>
                  </div>

                  <div v-else class="flex flex-col w-full mb-8">
                    <h5 class="mb-4">Select your platform</h5>
                    <div class="flex mb-4 relative">
                      <button
                        type="button"
                        class="inline-flex items-center cursor-pointer mr-4"
                        @click="video_type = 'YouTube'"
                      >
                        <span
                          :class="
                            video_type === 'YouTube'
                              ? 'font-semibold text-primary'
                              : ''
                          "
                          class="ml-1"
                          >YouTube</span
                        >
                      </button>
                      <div
                        class="w-px relative top-0.5 h-4 bg-bluegray-300 mr-4"
                      ></div>
                      <button
                        type="button"
                        class="inline-flex items-center cursor-pointer mr-4"
                        @click="video_type = 'Vimeo'"
                      >
                        <span
                          :class="
                            video_type === 'Vimeo'
                              ? 'font-semibold text-cyan-400'
                              : ''
                          "
                          class="ml-1"
                          >Vimeo</span
                        >
                      </button>
                      <div
                        :class="[
                          video_type === 'YouTube'
                            ? 'bg-expo_red translate-x-0'
                            : 'bg-cyan-400 translate-x-130',
                        ]"
                        class="absolute h-1 left-1 top-full transform translate-y-2 w-16 transition-all duration-300 ease-in-out rounded"
                      ></div>
                    </div>
                  </div>

                  <div v-if="!show_video_preview">
                    <div
                      class="h-12 w-1/2 rounded relative bg-white shadow border border-bluegray-300"
                    >
                      <h6
                        :class="inputLabelClass"
                        class="flex items-center group"
                      >
                        Video code
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-3 w-3 ml-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <div :class="infoClass" class="lg:w-80">
                          Only insert the {{ video_type }} video ID number or
                          code. For eg
                          <span
                            v-if="video_type === 'Vimeo'"
                            class="text-truegray-500 italic"
                          >
                            https://vimeo.com/<span class="text-expo_red"
                              >108799588</span
                            >
                          </span>
                          <span v-else class="text-truegray-300 italic">
                            https://www.youtube.com/watch?v=<span
                              class="text-expo_red"
                              >GCHLt0NDkdY</span
                            >
                          </span>
                        </div>
                      </h6>
                      <input
                        id="post_link_title"
                        v-model="video_code"
                        type="text"
                        name="post_link_title"
                        autocomplete="post_link_title"
                        :class="inputTextClass"
                      />
                    </div>
                  </div>

                  <!-- FILE INPUT TILE -->
                  <div
                    v-if="show_video_preview"
                    :style="{ width: '409px' }"
                    class="h-12 rounded relative bg-white shadow border border-bluegray-300 mt-4"
                  >
                    <h6
                      :class="inputLabelClass"
                      class="flex items-center group"
                    >
                      File name
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 ml-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div :class="infoClass">Give this video a title...</div>
                    </h6>
                    <input
                      id="post_link_title"
                      v-model="file_title"
                      type="text"
                      name="post_link_title"
                      autocomplete="post_link_title"
                      :class="inputTextClass"
                    />
                  </div>

                  <div class="w-full flex space-x-1 mt-4">
                    <button
                      v-if="!show_video_preview"
                      type="button"
                      class="py-2 px-3 rounded-md text-sm leading-4 font-medium text-white hover:bg-black bg-primary transition-all duration-100 ease-in-out"
                      @click="preview_video"
                    >
                      Preview
                    </button>
                    <button
                      v-if="show_video_preview"
                      type="button"
                      class="py-2 px-3 rounded-md text-sm leading-4 font-medium text-white hover:bg-black bg-primary transition-all duration-100 ease-in-out"
                      @click="save_video"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      class="py-2 px-3 rounded-md text-sm leading-4 font-medium hover:text-red-700 text-red-500 transition-all duration-100 ease-in-out"
                      @click="reset"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>

              <!-- PREVIEW AND TITLE -->
              <div v-if="file_preview" class="relative">
                <!-- Custom file Title -->
                <div class="flex justify-start items-center space-x-2 w-full">
                  <!-- FILE TYPE ICON -->
                  <svg
                    v-if="file_preview_type === 2"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <svg
                    v-if="file_preview_type === 1"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <!-- FILE INPUT TILE -->
                  <div
                    class="h-12 rounded relative bg-white shadow border border-bluegray-300"
                  >
                    <h6
                      :class="inputLabelClass"
                      class="flex items-center group"
                    >
                      File name
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 ml-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div :class="infoClass">Give this file a title...</div>
                    </h6>
                    <input
                      id="post_link_title"
                      v-model="file_title"
                      type="text"
                      name="post_link_title"
                      autocomplete="post_link_title"
                      :class="inputTextClass"
                    />
                  </div>
                  <!-- SAVE / CANCEL BUTTONS -->
                  <button
                    type="button"
                    class="ml-5 py-2 px-3 rounded-md text-sm leading-4 font-medium text-white hover:bg-black bg-primary transition-all duration-100 ease-in-out"
                    @click="save_file"
                  >
                    Upload
                  </button>
                  <button
                    type="button"
                    class="py-2 px-3 rounded-md text-sm leading-4 font-medium hover:text-red-700 text-red-500 transition-all duration-100 ease-in-out"
                    @click="reset"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- // RESOURCE LIST  -->
          <div
            v-if="
              loaded && resources.length > 0 && !video_preview && !file_preview
            "
            class="mt-4 w-full max-w-3xl"
          >
            <h6 class="text-truegray-500 italic mb-2">
              NOTE: After featuring media, you will need to reload your page to
              see it on your home page.
            </h6>
            <vue-nestable
              v-model="resources"
              :max-depth="1"
              @input="resourcesLoaded = true"
            >
              <vue-nestable-handle
                slot-scope="{ item }"
                :item="item"
                class="flex justify-start w-full space-x-2 bg-gray-50 text-bluegray-700 p-2 rounded border my-1"
              >
                <div class="flex justify-start items-center w-full">
                  <div class="flex justify-start items-center space-x-2">
                    <!-- Drag handle -->
                    <button
                      v-if="resources.length > 1"
                      class="drag"
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 hover:text-primary transition-all duration-100"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M3 7a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 13a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>

                    <!-- File type logo -->
                    <svg
                      v-if="item.type === 1"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <svg
                      v-if="item.type === 2"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <svg
                      v-if="item.type === 3"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                        clip-rule="evenodd"
                      />
                    </svg>

                    <span>{{ item.title }}</span>
                  </div>

                  <!-- Feature resource option -->
                  <span
                    :class="[
                      item.featured ? 'text-green-500' : 'opacity-25',
                      !item.featured && feat_resources_length > 7
                        ? 'cursor-not-allowed pointer-events-none'
                        : '',
                    ]"
                    class="flex space-x-2 items-center ml-auto mr-4 hover:text-green-500 hover:opacity-100 cursor-pointer transition-all duration-300"
                    @click="toggle_feature_resource(item)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="cursor-pointer hover:text-green-500 hover:opacity-100 transition-all duration-300 h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <p class="text-xs text-truegray-500">
                      Feature on home page
                    </p>
                  </span>
                </div>
                <div class="group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-300 hover:text-gray-500 cursor-pointer transition-all duration-100"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    @click="goToResource(item.id)"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <div
                    class="ml-2 text-xs absolute left-full top-0 w-24 z-50 border border-bluegray-300 rounded py-2 px-4 opacity-0 transition-all duration-300 transform pointer-events-none group-hover:opacity-100 bg-white text-center"
                  >
                    Edit Item
                  </div>
                </div>
              </vue-nestable-handle>
            </vue-nestable>
          </div>
          <p
            v-if="
              loaded &&
              resources.length === 0 &&
              !video_preview &&
              !file_preview
            "
            class="mt-4"
          >
            There are no resources to display
          </p>

          <!-- PAGINATION -->
          <div
            v-if="pagination.previouspage || pagination.nextpage"
            class="flex justify-between items-center h-10"
          >
            <span
              :disabled="!pagination.previouspage"
              :class="[
                !pagination.previouspage
                  ? 'opacity-25 cursor-not-allowed'
                  : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
              ]"
              class="text-gray-500"
              @click="getResources(pagination.previouspage)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 17l-5-5m0 0l5-5m-5 5h12"
                />
              </svg>
            </span>
            <span class="text-center text-xs"
              >Page {{ pagination.current_page }} of
              {{ pagination.total_pages }}</span
            >
            <span
              :disabled="!pagination.nextpage"
              :class="[
                !pagination.nextpage
                  ? 'opacity-25 cursor-not-allowed'
                  : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
              ]"
              class="text-gray-500"
              @click="getResources(pagination.nextpage)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
          </div>
        </div>
        <div
          v-if="!loaded"
          class="w-full rounded-xl min-h-screen flex flex-col items-center bg-bluegray-300 p-16"
        >
          <div class="mt-16 w-32">
            <lottie :options="loadingAnimationOptions" />
            <span class="text-white text-center">Loading Media</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { shared } from '../../../js/mixins/shared.js'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { VueNestable, VueNestableHandle } from 'vue-nestable'

export default {
  components: {
    VueNestable,
    VueNestableHandle,
  },

  mixins: [shared],

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      loaded: false,

      thinking: false,
      orderThinking: false,

      infoClass:
        'absolute left-full top-0 w-64 z-50 border border-bluegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-bluegray-300',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-bluegray-700',

      resources: [],
      resources_original: [],
      canSaveResources: false,
      feat_resources_length: 0,
      max_feat_resources: 7,

      // Add a new resource file...
      file_title: null,
      file_preview: null,
      file_preview_type: 0,
      file_preview_change: false,
      file_abort_error: false,

      video_type: 'YouTube',
      video_title: null,
      video_preview: false,
      video_code: null, // model
      show_video_preview: null,

      // Pagination
      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },
    }
  },

  computed: {
    ...mapGetters({
      client: 'shared/getClient',
      filterResourceBy: 'shared/getFilterResourceBy',
    }),
  },

  watch: {
    resources: {
      // String option
      handler: function (arr) {
        if (!this.loaded || arr.length < 1) {
          return
        }

        if (arr.length < 1 || this.resources_original.length < 1) {
          return
        }

        let thesame = this.isEquivalent(arr, this.resources_original)

        if (thesame) {
          this.$set(this, 'canSaveResources', false)
        } else {
          this.$set(this, 'canSaveResources', true)
        }
        let arrlength = 0
        arr.forEach((resource) => {
          if (resource.featured) {
            arrlength = arrlength + 1
          }
          this.$set(this, 'feat_resources_length', arrlength)
        })
      },

      deep: true,
    },

    video_type: {
      handler: function () {
        this.video_url_format(this.video_code)
      },

      deep: true,
    },

    video_code: {
      handler: function (val) {
        this.video_url_format(val)
      },

      deep: true,
    },
  },

  mounted() {
    this.getResources()
  },

  methods: {
    isEquivalent(a, b) {
      // If number of properties is different, objects are not equivalent
      if (a.length !== b.length) {
        return false
      }

      let num = 0
      let same = true
      a.forEach((i) => {
        if (i.id !== b[num].id) {
          same = false
        }
        num = num + 1
      })

      return same
    },

    save_resources() {
      axios
        .post('/api/auth/order-resources', {
          client: this.client,
          resources_order: this.resources,
          current_page: this.pagination.current_page,
        })
        .then((response) => {
          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
        })
    },

    toggle_feature_resource(item) {
      axios
        .post('/api/auth/toggle-feature-resource', { resource_id: item.id })
        .then((response) => {
          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
            if (response.data.success) {
              let arr = []
              this.resources.forEach((i) => {
                if (i.id === item.id) {
                  if (i.featured) {
                    i.featured = null
                  } else {
                    i.featured = 1
                  }
                }
                arr.push(i)
              })
              this.$set(this, 'resources', arr)
            }
          }
        })
    },

    save_video() {
      // confirm('saving new resource')
      this.thinking = true
      let submitData = new FormData()
      submitData.append('video_code', this.video_code)
      submitData.append('video_title', this.file_title)
      submitData.append('video_type', this.video_type)
      this.reset()
      axios
        .post('/api/auth/add-video-resource', submitData)
        .then((response) => {
          this.thinking = false
          let result = response.data
          if (result.success) {
            this.getResources()
            EventBus.$emit('reloadWebsiteSettings')
          }
          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
        })
    },

    preview_video() {
      if (!this.video_code) {
        return
      }

      let src = null
      if (this.video_type === 'YouTube') {
        src = 'https://www.youtube.com/embed/' + this.video_code
      }
      if (this.video_type === 'Vimeo') {
        src = 'https://player.vimeo.com/video/' + this.video_code
      }
      if (!src) {
        return
      }

      this.show_video_preview = src
    },

    video_url_format(code) {
      if (!code) {
        return
      }

      if (this.video_type === 'YouTube') {
        let str = code.replace('//www.youtube.com/embed/', '')
        this.resourceVideoEmbedUrl = '//www.youtube.com/embed/' + str
        code = str
      }

      if (this.video_type === 'Vimeo') {
        let str = code.replace('//player.vimeo.com/video/', '')
        this.resourceVideoEmbedUrl = '//player.vimeo.com/video/' + str
        code = str
      }

      return code
    },

    goToResource(id = 0) {
      this.$router.push({ path: '/manager/resource/' + id })
    },

    reset() {
      this.$set(this, 'file_preview_change', false)
      this.$set(this, 'file_preview', null)
      this.$set(this, 'file_title', null)
      this.$set(this, 'file_preview_type', 0)
      this.$set(this, 'file_abort_error', false)

      this.$set(this, 'video_title', '')
      this.$set(this, 'video_type', 'YouTube')
      this.$set(this, 'video_preview', false)
      this.$set(this, 'video_code', null)
      this.$set(this, 'show_video_preview', null)
    },

    file_change(type = 1) {
      if (type === 1) {
        this.$refs.selectFile.click()
        this.$set(this, 'video_preview', false)
      }
      if (type === 2) {
        this.reset()
        this.$set(this, 'video_preview', true)
      }
    },

    file_selected() {
      let input = this.$refs.selectFile
      let file = input.files

      // Determine if we have the correct file format and categorise it's type for upload...
      let document_formats =
        /(\.doc|\.docx|\.odt|\.pdf|\.tex|\.txt|\.rtf|\.wps|\.wks|\.wpd)$/i
      let image_formats = /(\.png|\.jpg|\.jpeg)$/i
      let type = 0
      if (document_formats.exec(file[0].name)) {
        type = 1
      }
      if (image_formats.exec(file[0].name)) {
        type = 2
      }
      if (type < 1) {
        this.$set(this, 'file_abort_error', true)
        return
      }

      this.$set(this, 'file_abort_error', false)

      if (file && file[0]) {
        // Kick out if this is an image file and is not valid:
        if (!this.validateImageFormat(file[0]) && type === 2) {
          return
        }

        if (file[0].size > 1024 * 1024 * 2 && type === 1) {
          EventBus.$emit('triggerAlertResponse', {
            status: 'error',
            message: 'File size too large. 2mb file size limit exceeded.',
          })
          return
        }

        let reader = new FileReader()
        reader.onload = () => {
          this.$set(this, 'file_preview_change', true)
          this.$set(this, 'file_preview', file[0])
          this.$set(this, 'file_preview_type', type)
          this.$set(
            this,
            'file_title',
            file[0].name.split('.').slice(0, -1).join('.'),
          )
        }
        reader.readAsDataURL(file[0])
        this.$emit('input', file[0])
      }
    },

    save_file() {
      // confirm('saving new resource')
      this.thinking = true
      let submitData = new FormData()
      submitData.append('resource', this.file_preview)
      submitData.append('resource_title', this.file_title)
      submitData.append('resource_type', this.file_preview_type)
      this.reset()
      axios.post('/api/auth/add-resource', submitData).then((response) => {
        this.thinking = false
        let result = response.data
        if (result.success) {
          this.getResources()
          EventBus.$emit('reloadWebsiteSettings')
        }
        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
      })
    },

    /**
        ============================ GET POSTS WITH FILTER OPTION ===============
    */

    getResources(gotopage = null) {
      let submitData = {}

      submitData.search = this.filterResourceBy
      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      // Get this post...
      axios.post('/api/auth/resources', submitData).then((response) => {
        let result = response.data
        if (result.success) {
          this.$set(this, 'resources_original', result.resources.data)
          this.$set(this, 'resources', result.resources.data)

          // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
          let total_pages =
            Math.floor(
              response.data.pagination.total /
                response.data.pagination.per_page,
            ) + 1
          this.$set(
            this.pagination,
            'current_page',
            response.data.pagination.current_page,
          )
          this.$set(this.pagination, 'total_pages', total_pages)
          this.$set(
            this.pagination,
            'per_page',
            response.data.pagination.per_page,
          )
          this.$set(
            this.pagination,
            'last_page_url',
            response.data.pagination.last_page_url,
          )
          this.$set(
            this.pagination,
            'next_page_url',
            response.data.pagination.next_page_url,
          )
          this.$set(
            this.pagination,
            'prev_page_url',
            response.data.pagination.prev_page_url,
          )
          this.$set(
            this.pagination,
            'path',
            response.data.pagination.next_page_path,
          )

          let prev = null
          if (this.pagination.prev_page_url) {
            prev = this.pagination.prev_page_url.split('=')
            prev = prev[1]
          }

          let next = null
          if (this.pagination.next_page_url) {
            next = this.pagination.next_page_url.split('=')
            next = next[1]
          }

          this.$set(this.pagination, 'previouspage', prev)
          this.$set(this.pagination, 'nextpage', next)
        }

        this.$set(this, 'loaded', true)
      })
    },
  },
}
</script>

<style scoped>
/*
* Style for nestable
*/
.nestable {
  position: relative;
}
.nestable-rtl {
  direction: rtl;
}
.nestable .nestable-list {
  margin: 0;
  padding: 0 0 0 40px;
  list-style-type: none;
}
.nestable-rtl .nestable-list {
  padding: 0 40px 0 0;
}
.nestable > .nestable-list {
  padding: 0;
}
.nestable-item,
.nestable-item-copy {
  margin: 10px 0 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
  margin-top: 0;
}
.nestable-item .nestable-list,
.nestable-item-copy .nestable-list {
  margin-top: 10px;
}
.nestable-item {
  position: relative;
}
.nestable-item.is-dragging .nestable-list {
  pointer-events: none;
}
.nestable-item.is-dragging * {
  opacity: 0;
  filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
  content: ' ';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(106, 127, 233, 0.274);
  border: 1px dashed rgb(73, 100, 241);
  -webkit-border-radius: 5px;
  border-radius: 5px;
}
.nestable-drag-layer {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
  left: auto;
  right: 0;
}
.nestable-drag-layer > .nestable-list {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
  padding: 0;
}
.nestable [draggable='true'] {
  /* cursor: move; */
}
.nestable-handle {
  /* display: inline; */
}
</style>
